import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './apiUrl';

@Injectable({
  providedIn: 'root',
})
export class InvoiceImportService {
  constructor(private api: ApiService) {}

  getOverview(storeId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportsOverview(storeId));
  }

  listImports(storeId: string, query: Record<string, string | number | undefined>): Observable<any> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params.set(k, String(v));
      }
    });
    const qs = params.toString();
    const path = ApiUrl.invoiceImportsList(storeId) + (qs ? `?${qs}` : '');
    return this.api.getRequest(path);
  }

  uploadPdfs(storeId: string, files: File[], supplierKey?: string): Observable<any> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f, f.name));
    if (supplierKey) {
      formData.append('supplierKey', supplierKey);
    }
    return this.api.postRequest(ApiUrl.invoiceImportsUpload(storeId), formData);
  }

  getImportDetail(storeId: string, importId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportById(storeId, importId));
  }

  getImportFileBlob(storeId: string, importId: string, fileId: string): Observable<Blob> {
    return this.api.getBlobRequest(ApiUrl.invoiceImportFile(storeId, importId, fileId));
  }

  reparse(storeId: string, importId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportReparse(storeId, importId), {});
  }

  archive(storeId: string, importId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportArchive(storeId, importId), {});
  }

  patchLine(storeId: string, importId: string, lineId: string, body: Record<string, unknown>): Observable<any> {
    return this.api.patchRequest(ApiUrl.invoiceImportLine(storeId, importId, lineId), body);
  }

  approveLine(
    storeId: string,
    importId: string,
    lineId: string,
    body: Record<string, unknown> = {}
  ): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportLineApprove(storeId, importId, lineId), body);
  }

  rejectLine(storeId: string, importId: string, lineId: string, reviewNote?: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportLineReject(storeId, importId, lineId), {
      reviewNote: reviewNote || undefined,
    });
  }

  undoAutoApproveLine(storeId: string, importId: string, lineId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportLineUndoAutoApprove(storeId, importId, lineId), {});
  }

  bulkApproveUnchanged(storeId: string, importId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportLinesBulkApprove(storeId, importId), {
      mode: 'unchanged',
    });
  }

  applyImport(storeId: string, importId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportApply(storeId, importId), {});
  }

  getGoodsReceipt(storeId: string, importId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportGoodsReceipt(storeId, importId));
  }

  createLineDraftProduct(
    storeId: string,
    importId: string,
    lineId: string,
    body: Record<string, unknown>
  ): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportLineDraftProduct(storeId, importId, lineId), body);
  }

  createDraftProductsBulk(
    storeId: string,
    importId: string,
    body: Record<string, unknown> = {}
  ): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportDraftProducts(storeId, importId), body);
  }

  pairImport(storeId: string, importId: string, otherImportId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportPair(storeId, importId), { otherImportId });
  }

  unpairImport(storeId: string, importId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportUnpair(storeId, importId), {});
  }

  getProductCostLayers(storeId: string, productId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportProductCostLayers(storeId, productId));
  }

  getTransportStock(storeId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportTransportStock(storeId));
  }

  testWholesalerEnrichment(storeId: string, supplierKey: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportWholesalerEnrichmentTest(storeId, supplierKey), {});
  }

  pullWholesalerEnrichment(storeId: string, supplierKey: string, listOnly = false): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportWholesalerEnrichmentPull(storeId, supplierKey), {
      listOnly,
    });
  }

  getWholesalerEnrichmentStatus(storeId: string, supplierKey: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportWholesalerEnrichmentStatus(storeId, supplierKey));
  }

  listDeliveryGroups(storeId: string, query: Record<string, string | number | undefined>): Observable<any> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params.set(k, String(v));
      }
    });
    const qs = params.toString();
    const path = ApiUrl.invoiceImportDeliveryGroups(storeId) + (qs ? `?${qs}` : '');
    return this.api.getRequest(path);
  }

  getDeliveryGroupDetail(storeId: string, groupKey: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportDeliveryGroup(storeId, groupKey));
  }

  bulkApproveDeliveryGroupUnchanged(storeId: string, groupKey: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportDeliveryGroupBulkApprove(storeId, groupKey), {
      mode: 'unchanged',
    });
  }

  applyDeliveryGroup(storeId: string, groupKey: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportDeliveryGroupApply(storeId, groupKey), {});
  }

  getStoreWholesalers(storeId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.invoiceImportsWholesalers(storeId));
  }

  putStoreWholesaler(storeId: string, supplierKey: string, body: Record<string, unknown>): Observable<any> {
    return this.api.putRequest(ApiUrl.invoiceImportsWholesalerByKey(storeId, supplierKey), body);
  }

  listSubstitutions(
    storeId: string,
    query: Record<string, string | number | boolean | undefined>
  ): Observable<any> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params.set(k, String(v));
      }
    });
    const qs = params.toString();
    const path = ApiUrl.invoiceImportSubstitutions(storeId) + (qs ? `?${qs}` : '');
    return this.api.getRequest(path);
  }

  createSubstitution(storeId: string, body: Record<string, unknown>): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportSubstitutions(storeId), body);
  }

  patchSubstitution(storeId: string, substitutionId: string, body: Record<string, unknown>): Observable<any> {
    return this.api.patchRequest(ApiUrl.invoiceImportSubstitutionById(storeId, substitutionId), body);
  }

  deleteSubstitution(storeId: string, substitutionId: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.invoiceImportSubstitutionById(storeId, substitutionId));
  }

  deactivateStoreWholesaler(storeId: string, supplierKey: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.invoiceImportsWholesalerByKey(storeId, supplierKey));
  }

  listUnassigned(page = 1, limit = 20): Observable<any> {
    return this.api.getRequest(`${ApiUrl.invoiceImportsUnassigned}?page=${page}&limit=${limit}`);
  }

  assignStore(importId: string, storeId: string): Observable<any> {
    return this.api.postRequest(ApiUrl.invoiceImportAssignStore(importId), { storeId });
  }

  listAdminWholesalerDefinitions(): Observable<any> {
    return this.api.getRequest(ApiUrl.adminWholesalerDefinitions);
  }

  putAdminWholesalerDefinition(supplierKey: string, body: Record<string, unknown>): Observable<any> {
    return this.api.putRequest(ApiUrl.adminWholesalerDefinitionByKey(supplierKey), body);
  }
}
